import {Controller, useFormContext} from 'react-hook-form';
import {SVG} from '../../../../../../constants/assetLists';
import {NetworkDetailsFormValues, NetworkNodeFieldPath} from '../../types';
import AppTextInput from '../../../../../../components/common/AppTextInput';
import AppMUIAutocomplete from '../../../../../../components/common/AppMUIAutoComplete';
import {
  COUNTRY_OPTIONS,
  getIsraelCitiesByState,
  ISRAEL_STATE_OPTIONS,
} from '../../data/israelLocations';

type Props = {
  selectedFieldPath: NetworkNodeFieldPath;
  expanded: boolean;
  onToggle: (id: string) => void;
};

function BuildingDetails({selectedFieldPath, expanded, onToggle}: Props) {
  const {control, setValue, watch} = useFormContext<NetworkDetailsFormValues>();
  const selectedState = watch(`${selectedFieldPath}.state`);
  const cityOptions = selectedState
    ? getIsraelCitiesByState(selectedState)
    : [];

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="w-[12px] h-[12px]"
          onClick={() => onToggle('building')}>
          <img
            src={expanded ? SVG.expandIcon : SVG.lessThanIcon}
            alt="caret"
            className="w-full h-full object-contain"
          />
        </button>

        <div className="flex items-center gap-1">
          <span className="text-base font-semibold text-raspberryRed">*</span>
          <p className="text-base font-semibold text-textColor">
            כתובת מטה חברה
          </p>
        </div>
      </div>

      {expanded && (
        <>
          <div className="flex gap-3">
            <Controller
              control={control}
              name={`${selectedFieldPath}.country`}
              render={({field, fieldState: {error}}) => (
                <div className="flex-1">
                  <AppMUIAutocomplete
                    label="מדינה"
                    options={[...COUNTRY_OPTIONS]}
                    getOptionLabel={option => option.label}
                    isOptionEqualToValue={(option, value) =>
                      option.value === value.value
                    }
                    size="small"
                    disabled
                    disableClearable
                    value={
                      COUNTRY_OPTIONS.find(
                        option => option.value === field.value,
                      ) ?? COUNTRY_OPTIONS[0]
                    }
                    onChange={(_event, option) =>
                      field.onChange(option?.value ?? '')
                    }
                    onBlur={field.onBlur}
                    required
                    error={!!error?.message}
                    helperText={error?.message}
                  />
                </div>
              )}
            />

            <Controller
              control={control}
              name={`${selectedFieldPath}.state`}
              render={({field, fieldState: {error}}) => (
                <div className="flex-1">
                  <AppMUIAutocomplete
                    label="אזור"
                    options={[...ISRAEL_STATE_OPTIONS]}
                    getOptionLabel={option => option.label}
                    isOptionEqualToValue={(option, value) =>
                      option.value === value.value
                    }
                    size="small"
                    value={
                      ISRAEL_STATE_OPTIONS.find(
                        option => option.value === field.value,
                      ) ?? null
                    }
                    onChange={(_event, option) => {
                      field.onChange(option?.value ?? '');
                      setValue(`${selectedFieldPath}.city`, '', {
                        shouldValidate: true,
                        shouldDirty: true,
                      });
                    }}
                    onBlur={field.onBlur}
                    required
                    error={!!error?.message}
                    helperText={error?.message}
                  />
                </div>
              )}
            />

            <Controller
              control={control}
              name={`${selectedFieldPath}.city`}
              render={({field, fieldState: {error}}) => (
                <div className="flex-1">
                  <AppMUIAutocomplete
                    label="עיר"
                    options={[...cityOptions]}
                    getOptionLabel={option => option.label}
                    isOptionEqualToValue={(option, value) =>
                      option.value === value.value
                    }
                    size="small"
                    value={
                      cityOptions.find(
                        option => option.value === field.value,
                      ) ?? null
                    }
                    onChange={(_event, option) =>
                      field.onChange(option?.value ?? '')
                    }
                    onBlur={field.onBlur}
                    required
                    error={!!error?.message}
                    helperText={error?.message}
                  />
                </div>
              )}
            />

            <Controller
              control={control}
              name={`${selectedFieldPath}.address`}
              render={({field, fieldState: {error}}) => (
                <div className="flex-1">
                  <AppTextInput
                    label="כתובת"
                    placeholder="כתובת"
                    required
                    className="border-none"
                    {...field}
                    error={error?.message}
                  />
                </div>
              )}
            />
          </div>

          <div className="flex gap-3">
            <Controller
              control={control}
              name={`${selectedFieldPath}.hpNumber`}
              render={({field, fieldState: {error}}) => (
                <div className="flex-1">
                  <AppTextInput
                    label="מספר ח.פ."
                    placeholder="מספר ח.פ."
                    className="border-none"
                    {...field}
                    error={error?.message}
                  />
                </div>
              )}
            />

            <Controller
              control={control}
              name={`${selectedFieldPath}.website`}
              render={({field, fieldState: {error}}) => (
                <div className="flex-1">
                  <AppTextInput
                    label="אתר אינטרנט"
                    placeholder="אתר אינטרנט"
                    className="border-none"
                    {...field}
                    error={error?.message}
                  />
                </div>
              )}
            />
          </div>

          <div className="flex gap-3">
            <Controller
              control={control}
              name={`${selectedFieldPath}.phone`}
              render={({field, fieldState: {error}}) => (
                <div className="flex-1">
                  <AppTextInput
                    label="טלפון חברה"
                    placeholder="טלפון חברה"
                    required
                    className="border-none"
                    {...field}
                    error={error?.message}
                  />
                </div>
              )}
            />

            <Controller
              control={control}
              name={`${selectedFieldPath}.email`}
              render={({field, fieldState: {error}}) => (
                <div className="flex-1">
                  <AppTextInput
                    label="אימייל חברה"
                    placeholder="אימייל חברה"
                    required
                    className="border-none"
                    {...field}
                    error={error?.message}
                  />
                </div>
              )}
            />
          </div>
        </>
      )}
    </div>
  );
}

export default BuildingDetails;
